import {ImageName} from "@mcleod/images";
import {ModelBillingHistory} from "@mcleod/ar/src/models/ModelBillingHistory";
import {CommonDialogs, CommonTooltips} from "@mcleod/common";
import {
    Button,
    ChangeEvent,
    ClickEvent,
    ContextMenuCreationEvent,
    CrudDecorator,
    DataSource,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    DataSourceModeChangeEvent,
    DropdownSelectionEvent,
    Image,
    ImageProps,
    Label,
    Layout,
    LayoutLoadEvent,
    LookupModelSearchEvent,
    LookupModelSelectionEvent,
    Panel,
    Snackbar,
    Table,
    TableRow,
    TableRowDisplayEvent,
    Tabset,
    Toast
} from "@mcleod/components";
import {Textbox} from "@mcleod/components/src/components/textbox/Textbox";
import {
    ActionPermission,
    Api,
    CompanySettings,
    Currency,
    DateUtil,
    GeneralSettings,
    getThemeColor,
    HorizontalAlignment,
    LogManager,
    McLeodClassicIntegration,
    Model,
    ModelRow,
    Navigation,
    PermissionsUtil,
    StringUtil
} from "@mcleod/core";
import {CurrencyUtil} from "@mcleod/core/src/CurrencyUtil";
import { getDispatchControlValue, LMEGeneralSettings, UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import {CarrierAssignmentStepper, CarrierQuickInfo, CarrierRatings, P44ShipmentTracking} from "@mcleod/powerbroker";
import {getTimeDiff} from "@mcleod/powerbroker/src/BrokeragePlanningUtil";
import {Dispatch} from "./asset/Dispatch";
import {EquipmentGroupValidation, EquipmentGroupValidationResult} from "./asset/EquipmentGroupValidation";
import {EquipmentGroupWarnings} from "./asset/EquipmentGroupWarnings";
import {ExtraPaySlideout} from "./asset/ExtraPaySlideout";
import {Preassignment, PreassignmentSlideoutProps} from "./asset/Preassignment";
import {AssignedMovementOverview} from "./AssignedMovementOverview";
import {AutogenLayoutMovement} from "./autogen/AutogenLayoutMovement";
import {AvailableMovementOverview} from "./AvailableMovementOverview";
import {BRLTLOrder} from "./brltl/BrltlUtil";
import {CancelStopSelectionSlideout} from "./CancelStopSelectionSlideout";
import {DispatchSlideout} from "./DispatchSlideout";
import {DispatchUtil} from "./DispatchUtil";
import {ManifestMovementQuickInfo} from "./ManifestMovementQuickInfo";
import {ModelMovement, RowMovement} from "./models/ModelMovement";
import {MovementRollback, RBAfterSaveProps} from "./MovementRollback";
import {MovementTabView} from "./MovementTabView";
import {Orders} from "./Orders";
import {OrderTabView} from "./OrderTabView";
import {PayHistory} from "./PayHistory";
import {StopList} from "./StopList";
import {StopProgress} from "./StopProgress";
import {WireUtil} from "./WireUtil";
import {YSplit} from "./YSplit";
import {LinkedAction, LinkedMoveSlideout} from "./planning/LinkedMoveSlideout";

export const CLEARED_STATUS_CODE = "D";

const log = LogManager.getLogger("lme.dispatch.Movement");
const equipWarningImageProps: Partial<ImageProps> = {
    id: "imageEquipWarning",
    name: ImageName.circleError,
    color: "error",
    height: 26,
    width: 22,
    marginLeft: 8,
    rowBreak: true
};

export class Movement extends AutogenLayoutMovement {
    private static viewMovementURL = "lme/dispatch/Movement?mode=update&key=";
    isCarrierAssigned: boolean;
    private allocationEnabled: boolean;
    private cancelling: boolean;
    public movementTabView: MovementTabView;
    private origBrkStatus = null;
    private callinRequiredBrkStatusCode: ModelRow;
    private equipmentId: string;
    private _validationResult: EquipmentGroupValidationResult = null;
    private isAssignable: boolean;
    private hasExperiment = GeneralSettings.hasEarlyAdoptersAccess();

    getMoveStatus = (): string => this.activeRow?.get("status", null);
    isAvailable = (): boolean => this.getMoveStatus() === "A";
    isCovered = (): boolean => this.getMoveStatus() === "C";
    isInProgress = (): boolean => this.getMoveStatus() === "P";
    isDelivered = (): boolean => this.getMoveStatus() === "D";
    isVoided = (): boolean => this.getMoveStatus() === "V";

    override onLoad() {
        this.tabsetMain["_header"].borderTopWidth = 0;
        this.setupStopRelationship();
        this.setupEquipmentAlerts();
        this.panelDimensionsAndSurcharge.visible = LMEGeneralSettings.getSingleton().getDispatchControl().dimension_surchg == "Y";
        this.textboxBrokerageStatus.addBeforeLookupModelSearchListener((event) => {
            if (this.sourceMovement.activeRow?.get("id"))
                event.filter = { movement_id: this.sourceMovement.activeRow.get("id") };
        });

        this.textboxBrokerageStatus.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                this.setTextboxBrokerageStatusColor(event.selection.get("planning_hex_color"));
                this.brokerageStatusOnChange();
            }
        });

        this.layoutCarrierRates.textboxMaxPayCalc.addBlurListener(async event => {
            const textbox = event.target as Textbox;
            this.sourceMovement.activeRow.set("max_pay_calc", textbox.getDataValue())
            await this.sourceMovement.post();
        })

        this.layoutCarrierRates.layoutTargetPay.addLayoutLoadListener(() => {
            this.layoutCarrierRates.layoutTargetPay.textboxTargetPayCalc.addBlurListener(async event => {
                const originalTargetPay = this.mainDataSource.activeRow.originalData["override_targetpay"] as Currency;
                const currentTargetPay = this.mainDataSource.activeRow.get("override_targetpay", null) as Currency; // "base_amount" always null
                if (originalTargetPay != null)
                    originalTargetPay["base_amount"] = null; // setting original "base_amount" to null to force comparison based on "amount"
                if (CurrencyUtil.compareCurrencies(originalTargetPay, currentTargetPay) != 0) {
                    const textbox = event.target as Textbox;
                    this.sourceMovement.activeRow.set("target_pay_calc", textbox.getDataValue());
                    await this.sourceMovement.post();
                }
            })
        });

        (this.layoutStopProgress as StopProgress).setStopsTable((this.layoutStopList as StopList).tableStop);

        this.layoutAvailableOverview.textboxXmitAccepted.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (!event.hasSelection()) return;
            this.activeRow.set("xmit_accepted", event.selection?.value);
            this.configurePanelLoadIcons(this.isAsset);
        });

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            const isUpdateMoveAfterSettledDenied: boolean = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED);
            const isTransferred: boolean = !this.mainDataSource.activeRow.isNull("xfer2settle_date");
            const isBilled: boolean = !this.mainDataSource.activeRow.isNull("orders.bill_date");

            const isUpdateMovementsAfterDeliveredDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);
            const isDelivered = this.activeRow?.get("status", null) === "D";

            if (isUpdateMovementsAfterDeliveredDenied && isDelivered) {

                await CommonDialogs.showDialog("Movement update not allowed because it is Delivered.");
                return false;

            }
            if (isUpdateMoveAfterSettledDenied && (isTransferred || isBilled)) {
                await CommonDialogs.showDialog("Movement update not allowed because it is Settled.");
                return false;
            }

            if (this._validationResult != null) {
                if (EquipmentGroupValidation.hasErrors(this._validationResult)) {
                    const equipmentGroupWarnings = await Layout.getLoadedLayout("lme/dispatch/asset/EquipmentGroupWarnings") as EquipmentGroupWarnings;
                    equipmentGroupWarnings.labelSaveWarning.visible = true;
                    equipmentGroupWarnings.display(this._validationResult);
                    const errorPanel = new Panel({ components: [equipmentGroupWarnings] });
                    CommonDialogs.showDialog(errorPanel, { title: "Error" });
                    return false;
                }
            }

            return true;
        }
        this.layoutMovementRouting.afterEquipmentValidation = (result) => this.handleEquipmentGroupValidationResults(result);
    }

    public get isAsset(): boolean {
        return this.activeRow?.get("brokerage", "N") === "N";
    }

    setTextboxBrokerageStatusColor(color: any) {
        if (color != null) {
            this.textboxBrokerageStatus.backgroundColor = getThemeColor(color);
            this.textboxBrokerageStatus.color = getThemeColor("default.reverse");
        } else {
            this.textboxBrokerageStatus.backgroundColor = getThemeColor("default.reverse");
            this.textboxBrokerageStatus.color = getThemeColor("default");
        }
    }

    brokerageStatusOnChange() {
        const row = this.mainDataSource.activeRow;
        if (row.get("brokerage_status") !== null && row.get("brokerage_status") !== row["originalData"]["brokerage_status"]) {
            const lmData = row.getFirstLookupModelData("brokerage_status");
            row.set("brokerage_status_color", lmData?.get("planning_color"));
            // potential bugs:
            //
            // The save button can be pressed before the DispatchSlideout promise is resolved and visible. We probably need some
            // Overlay.ts method that will add an empty gray overlay on the routing panel preventing user clicks until the promise is resolved.
            // Or we try to show the slideout as soon as possible without waiting for the promise to resolve.
            //
            // Do we revert the brokerage_status when DispatchSlideout is permed off and the user doen't have access?
            if (lmData != null && lmData.getBoolean("display_callin_screen", false)) {
                this.callinRequiredBrkStatusCode = row.getFirstLookupModelData("brokerage_status", null);
                this.buttonDispatchOnClick();
            }
        }
    }

    private unclearStops() {
        this.cancelling = true;
        return Api.update("lme/dispatch/unclear-stops",
            {
                "movement_id": this.sourceMovement.activeRow.get("id")
            }).then(response => {
                this.sourceMovement.search({ id: this.sourceMovement.activeRow.get("id") });
                Toast.showSuccessToast("Your movement was cancelled successfully");
                this.cancelling = false;
            }).catch(error => {
                this.cancelling = false;
                CommonDialogs.showError(error)
            });
    }

    sourceMovementAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.oldMode === DataSourceMode.NONE && event.newMode === DataSourceMode.UPDATE) {
            this.enforceActionPermissions(this.layoutAssignedOverview.tabsetCarrierDriver);
            this.enforceActionPermissions(this.layoutStopList);
            this.checkSegAllocEnabled();
        }
    }

    enforceActionPermissions(component?: Layout | TableRow | Tabset) {
        const isUpdateMovementsAfterDeliveredDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);
        const isUpdateMovementsAfterSettledDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED);
        const isDelivered = this.activeRow?.get("status", null) === "D";
        const isTransferred: boolean = !this.sourceMovement.activeRow.isNull("xfer2settle_date");
        const isBilled: boolean = !this.sourceMovement.activeRow.isNull("orders.bill_date");

        if ((isUpdateMovementsAfterDeliveredDenied == true && isDelivered)
            || (isUpdateMovementsAfterSettledDenied && (isTransferred || isBilled))) {
            const ignoreList = ['tabsetStopNotesRefNos', 'tabStopComments', 'tabStopRefNumbers'];
            const rootComponent = component ?? this;
            if (!ignoreList.includes(rootComponent.id)) {
                rootComponent.forEveryChildComponent(comp => {
                    if (ignoreList.includes(comp.id))
                        return;
                    else if (!(comp instanceof Label) || comp.hasClickListeners()) comp.enabled = false;

                    if (comp instanceof Table) {
                        const nestedTable: Table = comp as Table
                        nestedTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
                            const row: TableRow = (event as TableRowDisplayEvent).getTableRow();
                            row.forEveryChildComponent(child => {
                                if (!(child instanceof Label) || child.hasClickListeners()) child.enabledDuringUpdate = false;
                                if (child instanceof Tabset) {
                                    child.expandAll();
                                }
                            });

                        });
                    }
                });
            }
        }
    }

    private setComponentVisibility() {
        this.tabRates.visible = !this.isCarrierAssigned && this.activeRow.getBoolean("brokerage");
        this.tabTopMatch.visible = !this.isCarrierAssigned && !this.activeRow.getBoolean("is_brokerage_ltl") && this.activeRow.getBoolean("brokerage");
        if (this.tabTopMatch.visible) {
            this.layoutTopMatchList.initialize();
        }

        this.layoutAvailableOverview.visible = !this.isCarrierAssigned;
        if (this.layoutAvailableOverview.visible) {
            this.layoutAvailableOverview.panelMoveAndToll.visible = this.isAsset;
            this.layoutAvailableOverview.panelAssetPay.visible = this.isAsset;
            this.layoutAvailableOverview.panelAssetGeneralInfo.visible = this.isAsset;
            this.layoutAvailableOverview.panelAssetAssignmentInfo.visible = this.isAsset;
            this.layoutAvailableOverview.layoutMovementOfferTabs.visible = !this.isAsset;
            this.layoutAvailableOverview.panelOffers.visible = !this.isAsset;
            this.layoutAvailableOverview.panelTargetPay.visible = !this.isAsset;
            this.layoutAvailableOverview.labelDistance.visible = !this.isAsset;
            this.layoutAvailableOverview.labelMoveDistance.visible = !this.isAsset;
            this.layoutAvailableOverview.labelMI2.visible = !this.isAsset;
        }
        this.layoutAssignedOverview.visible = this.isCarrierAssigned;
        if (this.layoutAssignedOverview.visible) {
            this.layoutAssignedOverview.tabsetCarrierDriver.visible = !this.isAsset;
        }
        this.configurePanelLoadIcons(this.isAsset);
        this.buttonDispatch.visible = !this.isAsset || (this.isAsset && this.hasExperiment);
    }

    /**
     * Checks if Triumph (Hubtran) is both active and licensed.
     * Note: This now relies on a single computed field from the backend ("is_hubtran_licensed_and_active"),
     * rather than checking license settings and active status separately on the client.
     * The backend is responsible for determining both conditions.
     */
    isHubtranLicensedAndActive(): boolean {
        return this.activeRow.getBoolean("is_hubtran_licensed_and_active", false);
    }

    /** This is an event handler for the onContextMenuCreation event of dataheaderMovement. */
    dataheaderMovementOnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.activeRow == null || this.mainDataSource.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }

        const isBrokerageOrEarlyAdopter = this.hasExperiment || !this.isAsset;
        const isNotTransferred = this.activeRow.isNull("xfer2settle_date");
        const carrierId = this.activeRow.get("override_payee_id");

        const driverAssigned = this.sourceEquipmentGroup.activeRow && !this.sourceEquipmentGroup.activeRow.isNull("driver_id");
        const assignedStatus = this.isCovered() || this.isInProgress() || this.isDelivered();
        const isOnHold = this.activeRow.getBoolean("orders.on_hold", false);

        event.contextMenu.updateMenuLabels({
            convertToBrokerage: {
                visible: this.isAsset,
                enabled: !(this.isPreassigned() || assignedStatus) && isNotTransferred,
                disabledTooltip: !isNotTransferred
                    ? "Cannot convert movement that has been transferred to settlement"
                    : "Cannot convert movement with capacity assigned",
                onClick: () => this.convertMovement()
            },
            convertToAsset: {
                visible: !this.isAsset && (CompanySettings.getSingleton().isBoth() || CompanySettings.getSingleton().isAsset()),
                enabled: !assignedStatus && isNotTransferred,
                disabledTooltip: !isNotTransferred
                    ? "Cannot convert movement that has been transferred to settlement"
                    : "Cannot convert movement with assigned carrier",
                onClick: () => this.convertMovement()
            },
            createYSplit: {
                visible: isBrokerageOrEarlyAdopter,
                enabled: !this.isDelivered(),
                disabledTooltip: "Y-split is not available for Delivered movements",
                onClick: () => this._createYSplit()
            },
            unsplit: {
                visible: isBrokerageOrEarlyAdopter && this.activeRow.get("stop_dest.stop_type") === "SD",
                onClick: () => this.unsplit()
            },
            rollbackStops: {
                visible: isBrokerageOrEarlyAdopter && (this.isInProgress() || this.isDelivered()),
                enabled: isNotTransferred,
                disabledTooltip: "You cannot rollback a movement that has been transferred to settlements.",
                onClick: () => this._rollbackStops()
            },
            voidEmpty: {
              visible: "E" === this.activeRow?.getString("loaded", "L") && this.isAvailable(),
              onClick: () => this.voidEmptyMovement()
            },
            removeCarrier: {
                visible: (!this.isAsset || (this.isCarrierAssigned && this.intercompanyEnabled)) && assignedStatus,
                onClick: async () => {
                    await this.layoutAssignedOverview.movementActions.removeCarrier(this.activeRow);
                    await this.movementTabView.refreshData();
                }

            },
            sendIntercompany: {
                visible: !carrierId && this.intercompanyEnabled,
                enabled: !this.isDelivered() && !isOnHold,
                disabledTooltip: isOnHold
                    ? "Send Intercompany button is disabled because the order is on hold"
                    : "Intercompany not available for delivered movements",
                onClick: async () => this.assignIntercompanySlideout()
                        },
            // Triumph audit actions are only available for brokerage movements (non-asset) when licensed and active.
            sendToTriumphAudit: {
                visible: this.isHubtranLicensedAndActive() && !this.isAsset,
                enabled: this.isHubtranLicensedAndActive() && this.activeRow?.getBoolean("brokerage"),
                onClick: async () => this.sendTriumphPayAudit()
            },
            settlementDetails: {
                visible: this.isAsset && !this.isVoided(),
                enabled: isNotTransferred && driverAssigned,
                onClick: (event) => this._addExtraPay(event)
            },
            payHistory: {
                visible: this.isAsset && this.hasExperiment && !this.isVoided(),
                enabled: driverAssigned,
                onClick: (event) => this.addSettlementPay(event)
            }
        });
    }

    async convertMovement(movement: ModelRow = this.activeRow): Promise<boolean> {
        if (movement === null) return false;
        const input = { movement_id: movement.get('id'), manifest_id: movement.get('manifest_id')};
        await Api.post("lme/dispatch/convert-movement-type", input);
        const convertingTabID = this.movementTabView.layoutMovementTabs.currentTabId;
        this.movementTabView.layoutMovementTabs.currentTabId = null;
        this.movementTabView.layoutMovementTabs.selectedMovementId = convertingTabID;
        await this.movementTabView.refreshData();
        return true;
    }

    private _createYSplit() {
        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        const movementData = this.mainDataSource.activeRow;
        const ySplitData = {
            brokerage: movementData.get("brokerage") === "Y",
            ltl: movementData.get("ltl") === "Y",
            loaded: movementData.get("loaded") === "L",
            status: movementData.get("status") as string,
            transferredToSettlements: !!movementData.get("xfer2settle_date")
        };

        if (YSplit.validateYSplit(ySplitData)) {
            const orderId: string = movementData?.get("orders.id") as string;
            const manifestId: string = movementData?.get("manifest_id") as string;
            const movementId: string = movementData?.get("id") as string;
            YSplit.showSlideout({
                orderId: orderId,
                isCompanyBoth: isAsset && isBrokerage,
                manifestId: manifestId,
                movementId: movementId,
                doAfterSlideOut: async (success: boolean = false) => {
                    if (success) {
                        this.movementTabView.layoutMovementTabs.currentTabId = null;
                        await this.movementTabView.refreshData();
                        Toast.showSuccessToast("Y-split created successfully");
                    }
                }
            });
        }
    }

    unsplit() {
        Api.post("lme/dispatch/unsplit", {
            movement_id: this.mainDataSource.activeRow.get("id")
        }).then(async response => {
            if (!response.data[0].success) {
                Snackbar.showWarningSnackbar(response.data[0].message);
            } else {
                this.movementTabView.layoutMovementTabs.panelMovementTabInfo.removeAll();
                this.movementTabView.layoutMovementTabs.currentTabId = null;
                await this.movementTabView.refreshData();
                Toast.showSuccessToast("Unsplit successful");
            }
        })
    }

    private _addExtraPay(event: ClickEvent) {
        const slideout = new ExtraPaySlideout(this.mainDataSource, this.sourceEquipmentGroup);
        slideout.showSlideout((canceled: boolean) => {
            if (!canceled)
                this.sourceMovement.search({ id: this.sourceMovement.activeRow.get("id") });
        });
    }

    private addSettlementPay(event: ClickEvent) {
        const driver_id = this.sourceEquipmentGroup.activeRow?.get("driver_id");
        if (driver_id) {
            PayHistory.showSlideout(driver_id);
        }
    }

    private _rollbackStops() {
        const isUpdateMovementsAfterDeliveredDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);
        const isDelivered = this.activeRow?.get("status", null) === "D";
        if (isUpdateMovementsAfterDeliveredDenied == true && isDelivered === true) {
            CommonDialogs.showDialog("You do not have permission to rollback delivered movements.");
            return;
        }
        const stopCount: number = (this.layoutStopList as StopList)?.tableStop?.rowCount;
        const status: string = this.mainDataSource?.activeRow.get("status");
        if (this.isAsset) {
            MovementRollback.showMovementRollbackSlideout(this.activeRow, async (props: RBAfterSaveProps) => {
                if (props.cancelled) return;
                const panelContainer = new Panel({ align: HorizontalAlignment.CENTER });
                const lblSuccess = new Label({
                    text: "Stops were successfully rolled back",
                    fontSize: "xxlarge",
                    rowBreak: true
                });
                panelContainer.add(lblSuccess);
                if (props.orderId !== props.currentOrderId) {
                    const lblNaviPrefix = new Label({
                        text: "Go to current order: ",
                        fontSize: "xlarge",
                        paddingRight: 2,
                        rowBreak: false
                    });
                    const lblNaviLink = new Label({
                        text: `${props.currentOrderId}`,
                        fontBold: true,
                        fontSize: "xlarge",
                        paddingLeft: 2,
                        onClick: (event: ClickEvent) => {
                            const navProps = { orderId: props.currentOrderId, newTab: false };
                            OrderTabView.navigateTo(props.currentMoveId == null ? navProps : {
                                ...navProps,
                                key: props.currentMoveId
                            });
                        },
                        rowBreak: false
                    });
                    lblNaviLink.style.textDecoration = "underline";
                    panelContainer.add(lblNaviPrefix, lblNaviLink)
                }

                const crud = this.getParentLayout()?.getParentLayout()?.parent;
                if (crud && crud instanceof CrudDecorator) {
                    const newLayout = await Layout.getLoadedLayout("lme/dispatch/OrderTabView") as OrderTabView;
                    crud.removeAll();
                    crud.layout = newLayout;
                    crud.layoutLoaded();
                    crud.addLayout();
                    crud["defaultDataHeaderNavigateOnClose"]();
                } else {
                    OrderTabView.navigateTo({
                        ...(props.currentMoveId == null ? { key: props.currentOrderId } : { orderId: props.currentOrderId }),
                        ...(props.currentMoveId != null ? { key: props.currentMoveId } : {}),
                        newTab: false
                    });
                }

                Toast.showToast(panelContainer, null, { millisUntilDismissal: 10000 });
            });
        } else {
            const movementId = this.mainDataSource?.activeRow.get("id");
            if (status == "D" || stopCount > 2) {
                const slideout = new CancelStopSelectionSlideout(this.mainDataSource?.activeRow.get("id"));
                slideout.showSlideout((canceled: boolean) => {
                    if (!canceled) {
                        this.sourceMovement.search({ id: this.sourceMovement.activeRow.get("id") });
                        Toast.showSuccessToast("Your stops were successfully rolled back");
                    }
                });
            } else {
                Model.searchSingleRecord("lme/dispatch/unclear-stops", { "movement_id": movementId }).then((response) => {
                    const baseMessage = "Are you sure you want unclear the pickup and retain the carrier?";
                    const message = (response.getBoolean("has_detention", false))
                        ? response.getString("detention_text", baseMessage)
                        : baseMessage;
                    CommonDialogs.showYesNo(message, "Rollback Stops", {maxWidth: 650}).then(clickedYes => {
                        if (clickedYes) {
                            if (this.cancelling) return Promise.resolve();
                            return this.unclearStops();
                        }
                    });

                }).catch(error => {
                    Snackbar.showWarningSnackbar(error.messages[0]);
                });
            }
        }
    }

    private async voidEmptyMovement() {
        const orderId = this.activeRow?.get("orders.id") === "none" ? null : this.activeRow?.get("orders.id");
        const moveId = this.activeRow?.get("id");
        const voidEmpty = await CommonDialogs.showYesNo(`Are you sure you would like to void empty move ${moveId}?  Any unsaved changes will be lost.`, "Void Empty Movement");
        if (voidEmpty == undefined || !voidEmpty)
            return;

        const res = await Api.post("lme/dispatch/void-empty", { order_id: orderId, movement_id: moveId });

        if (!res.data[0].success) {
            Snackbar.showWarningSnackbar(res.data[0].message);
        } else {
            this.movementTabView.layoutMovementTabs.panelMovementTabInfo.removeAll();
            this.movementTabView.layoutMovementTabs.currentTabId = null;
            await this.movementTabView.refreshData(res.data[0].current_move_id);
            Toast.showSuccessToast("Void empty successful");
        }
    }

    private goToStopDetails() {
        this.tabsetMain.scrollToTab(this.tabOrderDetails);
    }

    private setupStopRelationship() {
        this.sourceStop.preventChangeNotifications = true;
        this.sourceStop.addAfterExecutionListener(event => {
            if (this.isCarrierAssigned)
                (this.layoutAssignedOverview as AssignedMovementOverview).setStops(this.sourceStop.data);
            else {
                const availableOverview = this.layoutAvailableOverview as AvailableMovementOverview;
                availableOverview.stopCountText = this.sourceStop.summaryData?.get("stop_count_text");
            }
        });
    }

    private setupEquipmentAlerts() {
        this.layoutEquipAlerts.layoutheaderEquipAlerts.visible = false;
    }

    /**
     * Checks if an order is an EDI inbound record (direction === "I")
     * @param orderId The order ID to check
     * @returns Promise<boolean> True if the order is EDI inbound, false otherwise
     */
    private async isEdiInboundOrder(orderId: string): Promise<boolean> {
        if (!orderId) return false;

        try {
            // Search for EDI billing details to get the direction
            const ediResponse = await Api.search("lme/datafusion/edi-billing-detail", { order_id: orderId });
            if (ediResponse && ediResponse.data && ediResponse.data.length > 0) {
                const ediRecord = ediResponse.data[0];
                const direction = ediRecord.get("direction");
                return direction === "I";
            }
        } catch (error) {
            // If there's no EDI record or an error, this is not an EDI order
            log.debug("No EDI record found for order:", orderId, error);
        }

        return false;
    }

    /** This is an event handler for the afterExecution event of sourceMovement.  */
    async sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        if (event?.getAction() == "search") {
            const custId = event.dataSource?.data[0]?.get("orders.customer_id");
            if (custId != null)
                this.panelCustomerInfo.dataSource.search({ id: custId });
            if (this.mainDataSource.activeRow != null) {
                this.isCarrierAssigned = this.mainDataSource.activeRow.get("override_payee_id") != null && !this.isAsset
                this.setComponentVisibility();
                this.layoutMovementCallins.visible = this.layoutMovementCallins.fillHeight = !this.isAsset;
                this.textboxBrokerageStatus.visible = !this.isAsset;
                if (!this.isAsset) {
                    this.setTextboxBrokerageStatusColor(this.mainDataSource.activeRow.get("brokerage_status_color"));
                }
                this.origBrkStatus = this.activeRow.get("brokerage_status", null);
                this.tabTracking.visible =
                    this.mainDataSource.activeRow.get("status", null) != "A"
                    && !this.activeRow.getBoolean("is_brokerage_ltl");
                if (this.isAsset) {
                    this.layoutTrackingHistory.visible = this.tabTracking.visible;

                    // This is done to prevent the overflowY of nestedLayout include the switchPositionCallin
                    const switchPositionCallin = this.layoutTrackingHistory.switchPositionCallin;
                    switchPositionCallin.setProps({ rowBreak: true, paddingLeft: 8 });
                    this.tabTracking.insert(switchPositionCallin, 1);

                    const xmitAccepted = this.activeRow.get("xmit_accepted");
                    if (xmitAccepted == undefined) {
                        this.activeRow.set("xmit_accepted", null);
                    }
                } else {
                    this.layoutMovementCallins.movementMap = this.layoutMovementMap;
                    this.layoutMovementCallins.movementId = this.activeRow?.get("id");
                }
                this.layoutAvailableOverview.layoutMovementOfferTabs.layoutPendingOffers.mainLayout = this;

                if (this.tabTopMatch.visible) {
                    this.layoutTopMatchList.textboxOrderId.visible =
                        this.layoutTopMatchList.textboxMovementId.visible =
                        this.layoutTopMatchList.layoutheaderTopMatch.visible = false;
                    this.layoutTopMatchList.sourceMovement.setRowsAndMode(DataSourceMode.UPDATE, [this.activeRow]);
                    this.layoutTopMatchList.setDefaultsFromDataSource().then(() => {
                        this.tabsetMain.addAfterTabSelectionListener((event) => {
                            if (event.newSelection.element.id == this.tabTopMatch.id &&
                                this.layoutTopMatchList.layoutTopMatch.sourceTopMatch.lastSearch == null &&
                                !this.layoutTopMatchList.layoutTopMatch.sourceTopMatch.isSearching &&
                                    this.layoutTopMatchList.searchpanelFilter.validateSimple(true, false)
                            ) {
                                this.layoutTopMatchList.sourceTopMatch.executeValidatedSearch()
                            }
                        });
                        if (history.state?.state?.additionalProps?.findCarrier === true) {
                            this.tabsetMain.scrollToTab(this.tabTopMatch);
                            this.tabTopMatch.select();
                        }
                    });
                }
                if (this.activeRow.isNull("orders.id")) {
                    this.panelOrderTab.enabled = false;
                    this.panelOrderTab.backgroundColor = "subtle.lightest";
                    this.labelOrderView.color = "subtle.darker";
                } else {
                    this.panelOrderTab.enabled = true;
                    this.panelOrderTab.backgroundColor = "backgroundSubtle";
                    this.labelOrderView.color = "primary";
                }

                const isHubtranLicensedAndActive = this.isHubtranLicensedAndActive();
                this.tabTriumphPay.visible = isHubtranLicensedAndActive && !this.isAsset;

                if (this.layoutTriumphPayTxAudit?.sourceHubtranInvoiceExceptions) {
                    this.layoutTriumphPayTxAudit.sourceHubtranInvoiceExceptions.addAfterSearchPlusChildrenListener((event: DataSourceExecutionEvent) =>
                        this.updateTriumphPayTabCount(event.dataSource));
                }
            }

            if (this["openPreassignment"] === true) {
                this.buttonManageAssignOnClick(null);
                this["openPreassignment"] = false;
            }
            this.movementTabView?.movementRefreshed();
            this.layoutTopMatchList.clearOfferDetails();
        } else if (event?.getAction() === DataSourceAction.UPDATE) {
            const brkStatus = this.activeRow.get("brokerage_status", null);
            if (!brkStatus != null && brkStatus !== this.origBrkStatus)
                this.showCarrierRatingSlideout();
        }
        this.labelPendingAssignment.visible = this.mainDataSource.activeRow?.getBoolean("has_pending_assignment", false);
        if (this.labelPendingAssignment.visible) {
            const unlockTime = this.mainDataSource.activeRow?.get("unlock_time");
            if (unlockTime && Math.ceil(getTimeDiff(new Date(unlockTime)) / (1000 * 60) % 60) <= 5) {
                this.labelPendingAssignment.backgroundColor = "error";
            }
            this.setTooltipPendingLabelCallback(this.labelPendingAssignment, this.mainDataSource.activeRow);
        }
        if (history.state?.state?.additionalProps?.assignCarrier == true) {
            if (!this.mainDataSource.activeRow.getBoolean("orders.on_hold", false)) {
                this.assignCarrierSlideout(this.buttonAssignCarrier, () => Orders.navigateTo(this.mainDataSource.activeRow.get("movement_order.order_id"), false));
                history.state.state = undefined;
            }
            else {
                Snackbar.showWarningSnackbar("Cannot assign carrier to a movement on hold.");
            }
        }
        if (history.state?.state?.additionalProps?.modifyPay == true) {
            this.layoutAssignedOverview.buttonModifyPay.clicked();
            history.state.state = undefined;
            history.replaceState({}, null, window.location.search);
        }

        await this.getEquipmentGroupData();
        this.isAssignable = await DispatchUtil.isAssignable(event.dataSource?.data[0],
            {
                btnAssign: this.buttonAssignCarrier,
                btnDispatch: this.buttonDispatch,
                btnManageAssign: this.buttonManageAssign
            },
            { equipmentId: this.equipmentId }
        );

        const moveData = DispatchUtil.getMoveData(event.dataSource?.data[0]);
        this.buttonAssignCarrier.visible = moveData.isBrokerage;
        this.buttonManageAssign.visible = !moveData.isBrokerage;

        if (this.layoutMovementRouting.activeRow) {
            // Collect individual trailer IDs from the equipment group
            const maximumTrailers = getDispatchControlValue("maximum_trailers");
            let trailer2Id: string | null = null;
            let trailer3Id: string | null = null;

            if (this.layoutMovementRouting.sourceEquipmentGroup.data.length > 1 && maximumTrailers >= 2) {
                trailer2Id = this.layoutMovementRouting.sourceEquipmentGroup.data[1].get("trailer_id", null);
            }
            if (this.layoutMovementRouting.sourceEquipmentGroup.data.length > 2 && maximumTrailers >= 3) {
                trailer3Id = this.layoutMovementRouting.sourceEquipmentGroup.data[2].get("trailer_id", null);
            }

            const validationResult = await EquipmentGroupValidation.validate({
                modelRow: this.layoutMovementRouting.activeRow,
                orderId: this.activeRow.get("orders.id"), movementId: this.activeRow.get("id"),
                tractorId: this.layoutMovementRouting.activeRow.get("tractor_id", null),
                driverId: this.layoutMovementRouting.activeRow.get("driver_id", null),
                ...(this.layoutMovementRouting.sourceEquipmentGroup.data.length > 1 ?
                    { driver2Id: this.layoutMovementRouting.sourceEquipmentGroup.data[1].get("driver_id", null) }
                    : {}),
                trailerId: this.layoutMovementRouting.activeRow.get("trailer_id", null),
                trailer2Id: trailer2Id,
                trailer3Id: trailer3Id
            });
            this.handleEquipmentGroupValidationResults(validationResult);
            this._validationResult = validationResult;
        }

        this.updateHoldStatus(
            event.dataSource?.data[0]?.getBoolean("orders.on_hold"),
            event.dataSource?.data[0]?.get("orders.hold_reason")
        );

        if (PermissionsUtil.isUserDeniedLayout("lme/dispatch/asset/PreassignmentStepper")) {
            this.buttonManageAssign.enabled = false;
            this.buttonManageAssign.tooltip = "You do not have permission to preassign movements";
        }

        if (this.movementTabView?.layoutMovementTabs.currentTabId == null) {
            this.movementTabView?.setActiveMovement(this.activeRow.get("id"));
        }

        if (!this.activeRow.isNull("manifest_id")) {
            this.textboxOrderNumber.visible = false;
            this.textboxManifestId.visible = true;
            this.imageManifest.visible = true;
            const manifestId = this.mainDataSource.activeRow.get("manifest_id");
            const movementId = this.mainDataSource.activeRow.get("id");
            if (this.imageManifest != null) {
                ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(this.imageManifest, movementId, manifestId);
            }
            this.layoutCarrierRates.panelManifestMovementCharges.visible = true;
            this.layoutCarrierRates.panelOrderCharges.visible = false;
            this.panelDimensionsAndSurcharge.visible = false;
            this.textboxRoundTrip.visible = false;
            this.textboxOrderValue.visible = false;
            this.panelCustomerInfo.visible = false;
            this.panelTemperatureRange.visible = false;
            this.textboxTeamsRequired.visible = false;
            if (this.activeRow.getBoolean("manifest_hold", false)) {
                this.buttonManageAssign.enabled = false;
                this.buttonManageAssign.disabledTooltip = "Manage Assignment button is disabled because the manifest is on hold.";
                this.buttonDispatch.enabled = false;
                this.buttonDispatch.disabledTooltip = "Dispatch button is disabled because the manifest is on hold.";
            }
        }
        this.showManifestComponents();
        if (!this.activeRow?.isNull("orders.id")) {
            const billingHistoryRecord = await new ModelBillingHistory().searchSingle({ order_id: this.activeRow?.getString("orders.id", null) });
            if (billingHistoryRecord) {
                // Check if this is an EDI inbound record and hide Invoice History tab accordingly
                const isEdiInbound = await this.isEdiInboundOrder(this.activeRow?.getString("orders.id", null));
                this.panelInvoiceHistoryTab.visible = !isEdiInbound;
                if (isEdiInbound) {
                    this.panelOrderTabs.remove(this.panelInvoiceHistoryTab, false);
                    // Remove border from Movement tab since it's now the last tab
                    if (this.panelMovementTab) {
                        this.panelMovementTab.borderRightWidth = 0;
                    }
                } else {
                    // Restore border on Movement tab for non-inbound records
                    if (this.panelMovementTab) {
                        this.panelMovementTab.borderRightWidth = 1;
                    }
                }
            } else {
                this.panelInvoiceHistoryTab.visible = false;
            }
        }
        this.imageLinked.visible = !this.activeRow.isNull("linked_id");
        if (!this.activeRow.isNull("linked_id")) {
            CommonTooltips.setTooltipContentLayout(this.imageLinked, null,
                "lme/dispatch/planning/LinkedMovesQuickInfoTable", null,
                { linked_id: this.activeRow.get("linked_id"), selected_movement_id: this.activeRow.get("id") }
            );
        }

        // Used to rebuild ellipsis
        this.dataHeader.syncTitle();
    }

    protected updateTriumphPayTabCount(dataSource?: DataSource) {
        const originalCaption = "Triumph Audit";
        const count = dataSource?.data?.length ?? 0;
        this.tabTriumphPay.caption = count > 0 ? `${originalCaption} (${count})` : originalCaption;
    }

    private showManifestComponents() {
        const isPartialLicensed = CompanySettings.get()?.partial_shipment_licensed;
        const partialShipmentEnabled = CompanySettings.get()?.partial_shipment_enabled;
        const manifestIdExists = !this.activeRow.isNull("manifest_id");
        const showManifestComponent = partialShipmentEnabled && isPartialLicensed && manifestIdExists;
        const manifestComponents = [ this.panelManifestCustInfo, this.textboxManifestOrderValue, this.textboxManifestTeamsRequired, this.panelManifestTemps ];
        manifestComponents.forEach(component => { component.visible = showManifestComponent; });
    }

    handleEquipmentGroupValidationResults(validationResult: EquipmentGroupValidationResult): void {
        if (this.isAssignable) {
            EquipmentGroupValidation.toggleButtonDispatch(this.activeRow.getDate("stop_origin.sched_arrive_early"), validationResult, this.buttonDispatch);
        }
        this.addEquipWarningImageToTab(validationResult);
        this.dataSource?.notifyHasChangedComponents();
        this._validationResult = validationResult;
    }

    addEquipWarningImageToTab(validationResult: EquipmentGroupValidationResult) {
        const hasErrorOrWarning = EquipmentGroupValidation.hasErrorsOrWarnings(validationResult);
        const existingWarning = this.tabEquipmentAssigned.findComponentById("imageEquipWarning") as Image;
        if (existingWarning != null) {
            existingWarning.visible = hasErrorOrWarning;
            if (hasErrorOrWarning) {
                (existingWarning.tooltipDynamicContent as Panel).findComponentByType(EquipmentGroupWarnings)?.display(validationResult);
            }
        } else {
            if (!hasErrorOrWarning)
                return;
            const warningImage = new Image(equipWarningImageProps);
            const equipmentGroupWarnings = Layout.getLayout("lme/dispatch/asset/EquipmentGroupWarnings") as EquipmentGroupWarnings;
            const tooltipPanel = new Panel({ components: [equipmentGroupWarnings] });
            equipmentGroupWarnings.addLayoutLoadListener(() => {
                equipmentGroupWarnings.display(validationResult);
                this.tabEquipmentAssigned.components[0].setProps({ rowBreak: false, fillRow: false });
                warningImage.tooltipDynamicContent = tooltipPanel;
                this.tabEquipmentAssigned.insert(warningImage, 1);
            });
        }
    }

    async configurePanelLoadIcons(isAsset: boolean) {
        this.layoutAvailableOverview.panelLoadIcons.visible = isAsset;
        if (isAsset) {
            this.layoutAvailableOverview.panelLoadIcons.components.forEach(component => component.visible = false);
            await this.populateWireAdvanceInfo();
            const xmitted2driver = this.activeRow.get("xmitted2driver");
            const xmitAccepted = this.activeRow.get("xmit_accepted");
            const zmitImageProps = this.getZMitImageProps(xmitted2driver === null || xmitted2driver === undefined, xmitAccepted);
            if (zmitImageProps.field) {
                zmitImageProps.field.name = zmitImageProps.name;
                zmitImageProps.field.tooltip = zmitImageProps.tooltip;
                zmitImageProps.field.visible = true;
            }
            if (!this.activeRow?.isNull("xfer2settle_date")) {
                this.layoutAvailableOverview.imageTransferToSettlement.visible = true;
                this.layoutAvailableOverview.imageTransferToSettlement.tooltip = "Transferred to Settlement on " + DateUtil.formatDateTime(this.sourceMovement.activeRow?.getDateTime("xfer2settle_date"));
            }
        }
    }

    private checkSegAllocEnabled() {
        this.allocationEnabled = this.sourceMovement.activeRow?.get("seg_alloc_enabled", false);
        this.textboxSegAllocCode.visible = this.allocationEnabled;
        this.textboxSegAllocCode.required = this.allocationEnabled;
        this.textboxOrdersSegAllocCode.visible = this.allocationEnabled;
    }

    private async populateWireAdvanceInfo() {
        const movementId = this.activeRow.get("id");
        const movementHasAdvance: boolean = this.activeRow.get("movement_has_wire_advance");
        const advanceComponent = this.layoutAvailableOverview.buttonDriverAdvances as Image;
        advanceComponent.visible = true;
        advanceComponent.name = movementHasAdvance ? ImageName.wireAdvances : ImageName.moneyInHand;
        advanceComponent.tooltip = movementHasAdvance ? "Issue Driver Advance" : "No Advance History";
        advanceComponent.fontSize = movementHasAdvance ? "24" : "20";
        if (advanceComponent.tooltip === "Issue Driver Advance") {
            WireUtil.setDetailHoverToDiscover(null, null, null, false, "Movement", movementId, advanceComponent);
        }
    }

    private getZMitImageProps(zMitNotTransmitted: boolean, zmitAccepted: string) {
        if (zMitNotTransmitted) {
            return {
                field: this.layoutAvailableOverview.imageXmitToDriver,
                name: ImageName.zMitNotTransmitted,
                tooltip: "Load Not Transmitted"
            }
        } else if (zmitAccepted === null || zmitAccepted === undefined) {
            return {
                field: this.layoutAvailableOverview.imageXmitToDriver,
                name: ImageName.zMitTransmitted,
                tooltip: "Load Transmitted"
            }
        } else if (zmitAccepted === 'Y') {
            return {
                field: this.layoutAvailableOverview.imageXmitAccepted,
                name: ImageName.zMitTransmittedAccepted,
                tooltip: "Load Accepted"
            }
        } else if (zmitAccepted === 'N') {
            return {
                field: this.layoutAvailableOverview.imageXmitAccepted,
                name: ImageName.zMitTransmittedRejected,
                tooltip: "Load Rejected"
            }
        }

        return { field: null, name: null, tooltip: null };
    }


    sourceMovementBeforeExecution(event: DataSourceExecutionEvent) {
        this.buttonManageAssign.enabled = false;
    }

    getBRLTLData() {
        const orderData: BRLTLOrder = {
            order_type_id: this.activeRow.get("orders.order_type_id"),
            revenue_code_id: this.activeRow.get("orders.revenue_code_id"),
            order_mode: this.activeRow.get("orders.order_mode")
        }
        return orderData
    }

    updateButton(button: Button, status: boolean, message?: string) {
        button.enabled = status;
        if (status === false) {
            button.disabledTooltip = message;
        }
    }

    updateHoldStatus(orderOnHold: boolean, holdReason: string) {
        const manifestOnHold = !this.activeRow.isNull("manifest_id") && this.activeRow.getBoolean("manifest_hold", false);
        const manifestHoldReason = "Manifest on hold, Please remove the hold before performing any action.";
        this.labelHoldBadge.visible = orderOnHold || manifestOnHold;
        if (orderOnHold) {
            this.labelHoldBadge.tooltip = holdReason;
        }
        else if (manifestOnHold) {
            this.labelHoldBadge.tooltip = manifestHoldReason;
        }
    }

    async sendTriumphPayAudit() {
        // Show download snackbar with spinner that persists until completion
        const snackbar = Snackbar.showDownloadSnackbar(
            "",
            "Upload in progress...",
            { persist: true, id: "triumphpay-audit-upload" }
        );

        const response = await Api.post("lme/dispatch/send-to-triumphpay-audit", { movement_id: this.activeRow.get("id") });
        const result = response.data[0];

        // Dismiss the progress snackbar
        snackbar?.dismiss();

        if (result.success) {
            Toast.showSuccessToast("Success!", result.success_message, ImageName.circleCheckEmpty);
            // If partial success, show the error in the snackbar as well
            if (result.partial_success && result.error_message) {
                Snackbar.showWarningSnackbar(result.error_message);
            }
        } else {
            // Only show the error snackbar if both failed
            if (result.error_message) {
                Snackbar.showWarningSnackbar(result.error_message);
            } else {
                Snackbar.showWarningSnackbar("Unknown error occurred.");
            }
        }
    }

    buttonManageAssignOnClick(event: ClickEvent) {
        if (this.layoutMovementRouting.activeRow && this._validationResult == null)
            return;
        const orderId = this.activeRow.get("movement_order.order_id");
        const manifestId = this.activeRow.get("manifest_id");
        const props: PreassignmentSlideoutProps = {
            movementId: this.activeRow.get("id"),
            orderId: orderId,
            manifestId: manifestId,
            preassigned: this.isPreassigned(),
            equipmentGroupId: this.activeRow.get("equipment_group_id", null),
            doAfterSlideout: async (name: string, showToast: boolean) => {
                await this.movementTabView.refreshData();
                if (showToast) {
                    Toast.showToast(Preassignment.getResultsToastPanel(orderId, name, manifestId), null, {
                        millisUntilDismissal: 7000,
                        targetPanel: (this.parent as Panel)
                    });
                }
            }
        };

        const equipmentDS = this.sourceEquipmentGroup;
        if (!this.activeRow.isNull("linked_id")) {
            props.linkedId = this.activeRow.get("linked_id");
        }
        if (equipmentDS.data.length > 0) {
            props.tractor = equipmentDS.data[0].get("tractor_id", null);
            props.driver1Data = equipmentDS.data[0].getFirstLookupModelData("driver_id", null)?.data;
            if (equipmentDS.data.length > 1) {
                props.driver2Data = equipmentDS.data[1].getFirstLookupModelData("driver_id", null)?.data;
            }
        }

        props.sourceEvent = event;
        Preassignment.showSlideout(props);
    }

    isPreassigned() {
        if (!this.activeRow.isNull("preassign_sequence") && this.sourceEquipmentGroup.data.length > 0) {
            if (EquipmentGroupValidation.hasErrors(this._validationResult)) {
                return false;
            }
            if (getDispatchControlValue("dispatch_by") === "D" && this.sourceEquipmentGroup.data.length > 0
                && this.sourceEquipmentGroup.data[0].isNull("driver_id")) {
                // if dispatch_by = 'D' and driver one is null, it is not preassigned
                return false;
            } else if (getDispatchControlValue("dispatch_by") === "T" && this.sourceEquipmentGroup.data.length > 0
                && this.sourceEquipmentGroup.data[0].isNull("tractor_id")) {
                // if dispatch_by = 'T' and Tractor is null, it is not preassigned
                return false;
            }
            return true;
        }
    }

    async getEquipmentGroupData() {
        if (this.activeRow?.get("preassign_sequence") < 0) {
            // Preassign sequence doesn't get set to null on update, so we need to do it here.
            this.activeRow.set("preassign_sequence", null);
            this.activeRow.resetOriginalData("preassign_sequence");
        }
        const movementRouting = this.layoutMovementRouting;
        if (this.activeRow != null && !this.activeRow.isNull("equipment_group_id")) {
            await movementRouting.tableMovementRouting.dataSource.search({
                id: this.activeRow.get("equipment_group_id")
            });
            const { tractor_id, driver_name, driver2_name, trailer_id, trailer2_id, trailer3_id } = movementRouting.mainDataSource.summaryData?.data;

            this.layoutAvailableOverview.textboxDriverTwoname.visible = !StringUtil.isEmptyString(driver2_name);

            this.activeRow.setValues({ tractor_id, driver_name, driver2_name, trailer_id, trailer2_id, trailer3_id });
            this.equipmentId = movementRouting.getEquipmentId();

            if (this.tabTracking.visible && this.isAsset) {
                const tractor_id = movementRouting.activeRow.get("tractor_id");
                const movement_id = this.activeRow.get("id");
                this.layoutTrackingHistory.search({ movement_id, callin_id: tractor_id });
                this.layoutMovementMap.search({ movement_id, include_positions: !this.layoutTrackingHistory.switchPositionCallin.checked });
                this.layoutTrackingHistory.switchPositionCallin.addChangeListener((event: ChangeEvent) => {
                    this.layoutMovementMap.search({ movement_id, include_positions: !this.layoutTrackingHistory.switchPositionCallin.checked });
                });
            }
        }

        const hasEquipment = this.isAsset && movementRouting.sourceEquipmentGroup.data?.some(eqp => Object.values(eqp.data).length > 1);
        this.tabEquipmentAssigned.visible = hasEquipment;
        this.tabEquipmentAlerts.visible = hasEquipment;

        if (this.isAsset) {
            this.searchEquipmentAlerts();
        }
        this.activeRow?.resetOriginalData();
    }

    buttonAssignCarrierOnClick(event: ClickEvent) {
        if (this.activeRow == null) return;
        return new ModelMovement().searchSingle({ id: this.activeRow.get("id") }, 'lme/powerbroker/CarrierAssignment').then((result: RowMovement) => {
            if (result != null) {
                if (result.isNull("override_payee_id")) {
                    this.assignCarrierSlideout(event.target as Button);
                } else {
                    return CommonDialogs.showYesNo("This movement has been covered. Reload the page to see the updated movement?", "Movement Updated")
                    .then((clickedYes) => {
                        if (clickedYes) {
                            return this.movementTabView.refreshData();
                        }
                    });
                }
            }
        });
    }

    private assignCarrierSlideout(carrierAssignBtn: Button, onCancel?: () => void) {
        CarrierAssignmentStepper.showAsSlideout({
            movementId: this.mainDataSource.activeRow?.get("id"),
            orderId: this.mainDataSource.activeRow?.get("movement_order.order_id"),
            carrierAssignmentButton: carrierAssignBtn,
            isOfferView: false,
            onClose: (canceled) => {
                const movementId = this.sourceMovement.activeRow.get("id");
                const isEdi: boolean = this.sourceMovement.owner?.["isEdi"];

                if (!canceled) {
                    isEdi ? Navigation.navigateTo("lme/datafusion/LoadTenderExpress") : this.sourceMovement.search({ id: movementId });
                } else if (onCancel) {
                    onCancel();
                }
            }
        });
    }

    async buttonDispatchOnClick() {
        const modelRow = this.mainDataSource.activeRow;
        if (modelRow == null)
            return;

        if (this.isAsset) {
            const { orderId, manifestId, title } = this.assetDispatchSlideoutTitle(modelRow);
            this.dispatchAsset(orderId, manifestId, title);
            return;
        }
        this.buttonDispatch.busy = true;
        this.showDispatchSlideout(modelRow).finally(() => this.buttonDispatch.busy = false);
    }

    private assetDispatchSlideoutTitle(modelRow: ModelRow<any>) {
        const orderId = modelRow.get("orders.id");
        const manifestId = modelRow.get("manifest_id");
        const originCity = StringUtil.toProperCase(modelRow.get("stop_origin.city_name"));
        const destCity = StringUtil.toProperCase(modelRow.get("stop_dest.city_name"));
        const originState = modelRow.get("stop_origin.state");
        const destState = modelRow.get("stop_dest.state");
        const routeInfo = `${originCity}, ${originState} to ${destCity}, ${destState}`;
        const title = !StringUtil.isEmptyString(manifestId) ? `Dispatch - Manifest ${manifestId}\t\t${routeInfo}` : `Dispatch - Order ${orderId}\t\t${routeInfo}`;
        return { orderId, manifestId, title };
    }

    async showDispatchSlideout(modelRow: ModelRow, previousStatus?: String, eventDrivenCallin?: boolean, current_stop_id?: string) {
        const isBrokerageLtlOrder = modelRow.getBoolean("is_brokerage_ltl");
        const dispatchType = (await Api.post("lme/dispatch/movement-dispatch-type", { movement_id: modelRow.get("id") })).data[0]?.["dispatch_type"];
        await (isBrokerageLtlOrder && StringUtil.equalsIgnoreCase(dispatchType, "P44") ? P44ShipmentTracking : DispatchSlideout).showSlideout(modelRow.get("id"), {
            movementOrId: modelRow.get("id"),
            orderId: modelRow.get("orders.id"),
            customerId: modelRow.get("orders.customer_id"),
            orderOnHold: modelRow.getBoolean("orders.on_hold", false),
            originCity: modelRow.get("stop_origin.city_name"),
            originState: modelRow.get("stop_origin.state"),
            destinationCity: modelRow.get("stop_dest.city_name"),
            destinationState: modelRow.get("stop_dest.state"),
            callinRequiredBrkStatusCode: this.callinRequiredBrkStatusCode,
            eventDrivenCallin: eventDrivenCallin,
            currentStopID: current_stop_id,
            manifestID: modelRow.get("manifest_id"),
            doAfterSlideOut: (decorator, saved, current_stop_id) => {
                if (saved) {
                    const prevBrokerageStatus = previousStatus ?? modelRow.get("brokerage_status");
                    this.mainDataSource.search({ id: this.activeRow.get("id") }).then((result) => {
                        if (result.modelRows.length > 0) {
                            const updatedModelRow = result.modelRows[0];
                            if (prevBrokerageStatus !== this.mainDataSource.activeRow.get("brokerage_status") &&
                                this.mainDataSource.activeRow.get("brokerage_status.display_callin_screen") == "Y") {
                                this.showDispatchSlideout(updatedModelRow, updatedModelRow.get("brokerage_status"), true, current_stop_id);
                            }
                        }
                    });
                }
            },
            onClose: (cancelled: boolean) => {
                if (!cancelled) {
                    const id = this.mainDataSource.activeRow.get("id");
                    this.movementTabView.refreshData();
                    this.layoutMovementCallins.layoutRecentCallins.mainDataSource.search({ movement_id: id });
                } else if (modelRow.getFirstLookupModelData("brokerage_status", null) !== null) {
                    this.mainDataSource.activeRow.revertToOriginalData(["brokerage_status", "brokerage_status_color"]);
                    this.setTextboxBrokerageStatusColor(this.mainDataSource.activeRow.get("brokerage_status_color"));
                }
            }
        });
    }

    async dispatchAsset(orderId: string, manifestId: string, title: string) {
        if (this.movementTabView.layoutMovementTabs.currentTabId) {
            Dispatch.openSlideout({
                equipmentId: this.equipmentId,
                equipmentType: getDispatchControlValue("dispatch_by"),
                orderId: orderId,
                manifestId: manifestId,
                slideoutTitle: title,
                doAfterSlideout: (hasChanged: boolean) => {
                    if (hasChanged) {
                        this.movementTabView.layoutMovementTabs.currentTabId = null;
                        this.movementTabView.refreshData();
                    }
                }
            });
        }
    }

    showCarrierRatingSlideout() {
        const triggerCode = LMEGeneralSettings.getSingleton().getDispatchControl().rating_trigger_code;
        if (!StringUtil.isEmptyString(triggerCode) && triggerCode === this.activeRow.get("brokerage_status")) {
            CarrierRatings.show({
                carrierRatingHeaderData: {
                    order_id: this.activeRow.get("movement_order.order_id"),
                    movement_id: this.activeRow.get("id"),
                    payee_id: this.activeRow.get("payee.id")
                },
                carrier_name: this.activeRow.get("payee.name")
            });
        }
    }

    async setTooltipPendingLabelCallback(pendingLabel: Label, modelRow: ModelRow) {
        pendingLabel.tooltipDynamicContent = async () => {
            const pendingTooltipData = await this.getPendingBadgeTooltipData(modelRow);
            if (pendingTooltipData) {
                return this.getPendingBadgeLayout(pendingTooltipData);
            }
            return null;
        };
    }

    async getPendingBadgeTooltipData(modelRow: ModelRow) {
        let tooltipData: ModelRow;
        if (modelRow?.isNull("pending_payee_id")) {
            const pendingCarrierType = modelRow.get("pending_carrier_type");
            const pendingCarrierNumber = modelRow.get("pending_carrier_nbr");
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow.get("id"),
                locked_user: modelRow.get("locked_user_id"),
                pending_carrier: true,
                ...(pendingCarrierType == "M" ? { icc_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "D" ? { dot_number: pendingCarrierNumber } : {}),
                ...(pendingCarrierType == "I" ? { intrastate_code: pendingCarrierNumber } : {})
            });
        } else {
            const result = await Api.search("lme/powerbroker/carriers-for-assignment", { id: modelRow?.get("pending_payee_id") });
            const payeeData = result?.data[0];
            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                movement_id: modelRow?.get("id"),
                locked_user: modelRow?.get("locked_user_id"),
                name: modelRow.get("pending_carrier_name") ?? payeeData.name,
                phone_number: payeeData.phone_number ?? "",
                icc_number: payeeData.icc_number,
                dot_number: payeeData.dot_number,
                intrastate_code: payeeData.intrastate_code,
                city: modelRow.get("pending_carrier_city") ?? "",
                state: modelRow.get("pending_carrier_state") ?? "",
                zip_code: modelRow.get("pending_carrier_zip") ?? "",
                pending_carrier: false,
                id: payeeData.id
            });
        }
        return tooltipData;
    }

    getPendingBadgeLayout(modelRow: ModelRow): Layout {
        const layout = Layout.getLayout("lme/powerbroker/CarrierQuickInfo") as CarrierQuickInfo;
        layout.addLayoutLoadListener((event: LayoutLoadEvent) => {
            event.layout.mainDataSource.data = [modelRow];
            layout.mainDataSource.rowIndex = 0;
        });
        layout.pendingCancelled = (lock_changed, user_can_unlock) => {
            if (lock_changed && user_can_unlock) {
                this.mainDataSource.search({ id: this.mainDataSource.activeRow.get("id") });
            }
        }
        return layout;
    }

    displayMovementUsersSlideout() {
        const urs = new UsersResponsibilitySlideoutTrans("lme/dispatch/MovementUsers", this.getMovementUsersSlideoutTitle(), this.getMovementUsersIdFilter(),
            this.getMovementUsersSharedFieldNames(), this.sourceMovement, this.sourceResponsibleHist);
        urs.show();
    }

    private getMovementUsersSlideoutTitle(): string {
        const name = this.sourceMovement.activeRow?.get("id");
        return "Movement Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getMovementUsersIdFilter(): any {
        const id = this.sourceMovement.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getMovementUsersSharedFieldNames(): string[] {
        return ["dispatcher_user_id", "operations_user", "fleet_manager"];
    }

    /** This is an event handler for the onClick event of buttonOrderDetails.  */
    buttonOrderDetailsOnClick(event: ClickEvent) {
        const orderPanel = new CrudDecorator({
            layout: Layout.getLayout("lme/dispatch/Orders") as Orders,
            mode: DataSourceMode.UPDATE,
            key: this.mainDataSource?.activeRow.get("movement_order.order_id"),
            headerProps: {
                showClose: true,
                showSaveAndClose: true,
                onExecute: (row: ModelRow<any>) => {
                    this.sourceMovement.search({ id: this.sourceMovement.activeRow.get("id") })
                }
            }
        });
        orderPanel.slideIn({ speed: 200 });
    }

    buttonClassicModeOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.dsp.EntryMovement", this.sourceMovement.activeRow?.get("id"));
    }

    private searchEquipmentAlerts() {
        const units = [];
        const equipData = this.sourceEquipmentGroup.data;
        equipData.forEach((row) => {
            if (!row.isNull("tractor_id")) units.push(row.get("tractor_id"));
            if (!row.isNull("trailer_id")) units.push(row.get("trailer_id"));
            if (!row.isNull("driver_id")) units.push(row.get("driver_id"));
        })
        if (units.length > 0) {
            this.layoutEquipAlerts.search({ units: units });
        }
    }

    public static navigateTo(id: string): void {
        Navigation.navigateTo(this.viewMovementURL + id, { newTab: true });
    }

    public get intercompanyEnabled(): boolean {
        return this.activeRow?.get("intercompany_enabled", false) === true;
    }

    private assignIntercompanySlideout(carrierAssignBtn?: Button, onCancel?: () => void) {
        const carrierId = this.mainDataSource.activeRow?.get("override_payee_id");

        CarrierAssignmentStepper.showAsSlideout({
            movementId: this.mainDataSource.activeRow?.get("id"),
            orderId: this.mainDataSource.activeRow?.get("movement_order.order_id"),
            carrierId: carrierId,
            isIntercompany: true,
            isOfferView: false,
            carrierAssignmentButton: carrierAssignBtn,
            onClose: (canceled) => {
                const movementId = this.sourceMovement.activeRow.get("id");

                if (!canceled) {
                    this.sourceMovement.search({ id: movementId });
                } else if (onCancel) {
                    onCancel();
                }
            }
        });
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxSegAllocCode. */
    textboxSegAllocCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.alloc_type = "M";
        event.filter.is_active = "Y";
    }

    override displayInViewModeAfterLoad() {
        super.displayInViewModeAfterLoad();
        this.dataHeader.displayInViewMode();
        this.panelOrderTab.enabled = true;
        this.labelOrderView.enabled = true;
        this.panelMovementTab.enabled = true;
        this.labelMovementView.enabled = true;
        this.panelInvoiceHistoryTab.enabled = true;
        this.labelInvoiceHistory.enabled = true;
    }

    imageLinkedOnClick(event: ClickEvent) {
        new LinkedMoveSlideout({ linkedId: this.activeRow.get("linked_id"), linkedAction: LinkedAction.EDIT });
    }
}
